import React, { useState } from "react";
import Modal from "../Admin/Modal/Modal";
import { ROLES } from "../../constants/roles";

const SIGNUP_ROLE_OPTIONS = [
  { value: ROLES.PROMOTER, label: "Promoter" },
  { value: ROLES.MERCHANDISER, label: "Merchandiser" },
];

export default function SignupForm({ onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: ROLES.PROMOTER,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccessModal(true);
      } else {
        setError(data.error || "Registration failed");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onSuccess();
        }}
        size="small"
        closeOnOverlayClick={false}
      >
        <div className="signup-success-modal">
          <h3 className="signup-success-title">Registration successful</h3>
          <p className="signup-success-text">
            Your account is created and is awaiting admin approval.
          </p>
          <div className="signup-success-actions">
            <button
              type="button"
              className="submit signup-success-btn"
              onClick={() => {
                setShowSuccessModal(false);
                onSuccess();
              }}
            >
              OK
            </button>
          </div>
        </div>
      </Modal>

      <div className="login-header">
        <button className="back" onClick={onBack} aria-label="Back">
          ←
        </button>
        <div className="header-title">Sign Up</div>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        <label className="login-field-label">
          Full Name <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          placeholder="Full Name"
          value={formData.fullName}
          onChange={(e) =>
            setFormData({ ...formData, fullName: e.target.value })
          }
          required
          disabled={loading}
        />
        <label className="login-field-label">
          Email <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          disabled={loading}
        />
        <label className="login-field-label" htmlFor="signup-role">
          Role <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <select
          id="signup-role"
          className="login-select"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          required
          disabled={loading}
        >
          {SIGNUP_ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="login-field-label">
          Password <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <div className="password-input-wrap">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            required
            disabled={loading}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((value) => !value)}
            disabled={loading}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <svg
                className="password-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 3l18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M10.58 10.58a2 2 0 1 0 2.83 2.83"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.88 5.09A10.94 10.94 0 0 1 12 4.9c7 0 10.5 7.1 10.5 7.1a16.4 16.4 0 0 1-4.16 4.86M6.61 6.61A16.57 16.57 0 0 0 1.5 12s3.5 7.1 10.5 7.1c1.53 0 2.94-.33 4.2-.9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                className="password-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1.5 12s3.5-7.1 10.5-7.1S22.5 12 22.5 12 19 19.1 12 19.1 1.5 12 1.5 12z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            )}
          </button>
        </div>
        <label className="login-field-label">
          Confirm Password <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <div className="password-input-wrap">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData({ ...formData, confirmPassword: e.target.value })
            }
            required
            disabled={loading}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowConfirmPassword((value) => !value)}
            disabled={loading}
            aria-label={
              showConfirmPassword
                ? "Hide confirm password"
                : "Show confirm password"
            }
          >
            {showConfirmPassword ? (
              <svg
                className="password-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 3l18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M10.58 10.58a2 2 0 1 0 2.83 2.83"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.88 5.09A10.94 10.94 0 0 1 12 4.9c7 0 10.5 7.1 10.5 7.1a16.4 16.4 0 0 1-4.16 4.86M6.61 6.61A16.57 16.57 0 0 0 1.5 12s3.5 7.1 10.5 7.1c1.53 0 2.94-.33 4.2-.9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                className="password-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1.5 12s3.5-7.1 10.5-7.1S22.5 12 22.5 12 19 19.1 12 19.1 1.5 12 1.5 12z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            )}
          </button>
        </div>

        {error && (
          <div
            className="alert-banner alert-error"
            role="alert"
            aria-live="assertive"
          >
            <div className="alert-icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 9v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 17h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                  stroke="currentColor"
                  strokeWidth="0"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="alert-body">
              <div className="alert-text">{error}</div>
            </div>
            <button
              type="button"
              className="alert-dismiss"
              aria-label="Dismiss"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        <button className="submit" type="submit" disabled={loading}>
          {loading ? "Creating Account..." : "Sign Up"}
        </button>

        <div className="signup-link">
          Already have an account?{" "}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            Sign In
          </a>
        </div>
      </form>
    </>
  );
}
